import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Clock, Zap, MapPin, User, PhoneCall, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`; }

/**
 * Fallback-accept queue: shows Express offers that this driver was once
 * prompted for but hasn't (yet) accepted. As long as the 70 s grace window
 * is still open, the driver can grab the offer from here even if they
 * missed the 12 s call notification or if it was reassigned to another
 * driver in the meantime.
 */
export default function ExpressPendingQueue({ onAccepted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState("");
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    api.get("/driver/express/pending")
      .then(({ data }) => setItems(data.pending || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // Poll every 8 s so offers appear/expire naturally.
    const poll = setInterval(load, 8000);
    // 1 Hz tick for the countdown.
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [load]);

  const accept = async (b) => {
    setAccepting(b.id);
    try {
      await api.post(`/express/${b.id}/accept`);
      toast.success("Course acceptée ✓");
      window.dispatchEvent(new CustomEvent("trigo:start-tracking", { detail: { bookingId: b.id } }));
      window.dispatchEvent(new CustomEvent("trigo:express-accepted", { detail: { bookingId: b.id } }));
      setItems((prev) => prev.filter((x) => x.id !== b.id));
      onAccepted?.(b);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      // If it just expired or was taken → refresh so the row disappears.
      load();
    } finally { setAccepting(""); }
  };

  // Recompute seconds_left client-side each tick
  const rows = items.map((b) => {
    const expiresMs = b.expires_at ? new Date(b.expires_at).getTime() : 0;
    const secondsLeft = Math.max(0, Math.round((expiresMs - now) / 1000));
    return { ...b, secondsLeft };
  }).filter((b) => b.secondsLeft > 0);

  if (loading) {
    return (
      <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#0F2B46]" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="express-pending-empty">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#F2A93B]" />
          <h3 className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Courses Express en attente</h3>
        </div>
        <p className="text-xs text-[#5B6B7A] mt-1">Aucune course en attente. Les offres manquées apparaissent ici pendant 1 min 10 s.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#F2A93B] border-2 rounded-2xl p-4" data-testid="express-pending-queue">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-5 h-5 text-[#F2A93B] animate-pulse" />
        <h3 className="text-base font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Courses Express en attente ({rows.length})</h3>
        <span className="text-[10.5px] text-[#5B6B7A] ml-auto">Vous pouvez toujours accepter pendant 1 min 10 s</span>
      </div>
      <div className="space-y-2">
        {rows.map((b) => {
          const isCurrent = !!b.is_currently_assigned;
          const urgent = b.secondsLeft <= 15;
          return (
            <div key={b.id} className={`p-3 rounded-xl border-2 flex flex-wrap items-center gap-3 ${urgent ? "border-[#EF4444] bg-red-50" : "border-[#F2A93B] bg-[#FEF7EC]"}`} data-testid={`express-pending-row-${b.id}`}>
              <div className="w-11 h-11 rounded-xl bg-[#0F2B46] text-[#F2A93B] font-extrabold text-lg flex items-center justify-center shrink-0" style={{ fontFamily: "Sora" }}>
                {b.secondsLeft}s
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-[#0F2B46] text-sm" style={{ fontFamily: "Sora" }}>{fmtXAF(b.total_amount)}</span>
                  <span className="text-[11px] text-[#5B6B7A]">· {Number(b.distance_km || 0).toFixed(1)} km · {Math.round(b.duration_min || 0)} min</span>
                  {isCurrent ? (
                    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded"><PhoneCall className="w-2.5 h-2.5" /> Assigné à vous</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded"><Clock className="w-2.5 h-2.5" /> Rattrapage possible</span>
                  )}
                </div>
                <div className="text-[11.5px] text-[#0F2B46] mt-0.5 flex items-center gap-1 truncate">
                  <MapPin className="w-3 h-3 text-[#F2A93B] shrink-0" />
                  <span className="truncate">{b.pickup_address || "?"} → {b.dropoff_address || "?"}</span>
                </div>
                {b.user_name && (
                  <div className="text-[11px] text-[#5B6B7A] flex items-center gap-1 mt-0.5"><User className="w-3 h-3" /> {b.user_name}</div>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => accept(b)}
                disabled={accepting === b.id}
                className="bg-[#3FAE7A] text-white hover:bg-emerald-700 font-bold"
                data-testid={`express-pending-accept-${b.id}`}
              >
                {accepting === b.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Accepter
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
